// Readable booking history (presentation only). The server's history entries
// (kind + details) are rendered with approved wording, unit numbers instead of
// identifiers, and instants in Asia/Riyadh business time.

type Locale = 'ar' | 'en';
type Text = Readonly<Record<Locale, string>>;

export interface HistoryEntry {
  readonly kind: string;
  readonly recordedAt: string;
  readonly reason: string | null;
  readonly details: readonly { readonly field: string; readonly value: string | null }[];
}

const KIND: Readonly<Record<string, Text>> = {
  lifecycle: { en: 'Status changed', ar: 'تغيّرت الحالة' },
  assignment: { en: 'Unit assignment', ar: 'تخصيص الوحدة' },
  extension: { en: 'Extended', ar: 'تمديد' },
  price_change: { en: 'Price changed', ar: 'تغيّر السعر' },
  terms_change: { en: 'Terms corrected', ar: 'تصحيح البنود' },
  closure: { en: 'Closed', ar: 'إغلاق' },
  source: { en: 'Source recorded', ar: 'تسجيل المصدر' },
};

const STAY: Readonly<Record<string, Text>> = {
  CHECK_IN: { en: 'Stay: checked in', ar: 'الإقامة: تسجيل الدخول' },
  CHECK_OUT: { en: 'Stay: checked out', ar: 'الإقامة: تسجيل الخروج' },
  MOVE_OUT: { en: 'Stay: moved out of unit', ar: 'الإقامة: الخروج من الوحدة' },
  MOVE_IN: { en: 'Stay: moved into unit', ar: 'الإقامة: الدخول إلى الوحدة' },
};

const STATUS: Readonly<Record<string, Text>> = {
  DRAFT: { en: 'Draft', ar: 'مسودة' },
  RESERVED: { en: 'Reserved', ar: 'محجوز' },
  ACTIVE: { en: 'Active', ar: 'نشط' },
  COMPLETED: { en: 'Completed', ar: 'مكتمل' },
  CANCELLED: { en: 'Cancelled', ar: 'ملغى' },
  ENDED_EARLY: { en: 'Ended early', ar: 'منتهٍ مبكرًا' },
  NO_SHOW: { en: 'No-show', ar: 'عدم حضور' },
};

const TERMS: Readonly<Record<string, Text>> = {
  guests: { en: 'guests', ar: 'عدد الضيوف' },
  agreedDeposit: { en: 'deposit', ar: 'التأمين' },
  scheduledCheckinTime: { en: 'check-in time', ar: 'وقت الدخول' },
  occupantId: { en: 'occupant', ar: 'الشاغل' },
  initialTotal: { en: 'agreed total', ar: 'الإجمالي المتفق عليه' },
};

const WORDS = {
  unit: { en: 'Unit', ar: 'الوحدة' },
  end: { en: 'End', ar: 'النهاية' },
  added: { en: 'added', ar: 'المضاف' },
  newTotal: { en: 'new total', ar: 'الإجمالي الجديد' },
  created: { en: 'Created', ar: 'أُنشئ' },
} as const;

// "04 Nov 2026, 09:50" in Asia/Riyadh (Gregorian, Latin digits in Arabic too).
export function riyadhDateTime(locale: Locale, instant: string | null | undefined): string {
  if (!instant) return '–';
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return instant;
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-ca-gregory-nu-latn' : 'en-GB', {
    timeZone: 'Asia/Riyadh',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

const detail = (entry: HistoryEntry, field: string) => entry.details.find((d) => d.field === field)?.value ?? null;

export function historyTitle(entry: HistoryEntry, locale: Locale): string {
  if (entry.kind === 'actual_stay') {
    const event = detail(entry, 'event') ?? '';
    return STAY[event]?.[locale] ?? STAY.CHECK_IN?.[locale] ?? event;
  }
  return KIND[entry.kind]?.[locale] ?? entry.kind;
}

export function historySummary(
  entry: HistoryEntry,
  locale: Locale,
  unitNumber: (unitId: string) => string | undefined,
): string {
  const unit = (id: string | null) => (id ? `${WORDS.unit[locale]} ${unitNumber(id) ?? '—'}` : '');
  const status = (code: string | null) => (code ? (STATUS[code]?.[locale] ?? code) : WORDS.created[locale]);
  switch (entry.kind) {
    case 'lifecycle': {
      const from = detail(entry, 'from');
      return from
        ? `${status(from)} → ${status(detail(entry, 'to'))}`
        : `${WORDS.created[locale]} → ${status(detail(entry, 'to'))}`;
    }
    case 'assignment':
      return `${unit(detail(entry, 'unitId'))} · ${detail(entry, 'start') ?? '–'} → ${detail(entry, 'end') ?? '–'}`;
    case 'extension':
      return `${WORDS.end[locale]} ${detail(entry, 'previousEnd') ?? '–'} → ${detail(entry, 'newEnd') ?? '–'} · ${WORDS.added[locale]} ${detail(entry, 'addedAmount') ?? '–'} · ${WORDS.newTotal[locale]} ${detail(entry, 'newTotal') ?? '–'}`;
    case 'price_change':
      return `${detail(entry, 'previousTotal') ?? '–'} → ${detail(entry, 'newTotal') ?? '–'}`;
    case 'terms_change':
      return (detail(entry, 'fields') ?? '')
        .split(',')
        .filter(Boolean)
        .map((field) => TERMS[field]?.[locale] ?? field)
        .join(', ');
    case 'closure':
      return status(detail(entry, 'outcome'));
    case 'actual_stay':
      return `${unit(detail(entry, 'unitId'))} · ${riyadhDateTime(locale, detail(entry, 'occurredAt'))}`;
    default:
      return entry.details.map((d) => `${d.field}: ${d.value ?? '—'}`).join(' · ');
  }
}
